import * as React from 'react'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {
  DataObjectId,
  isSchedule,
  Schedule,
  ScheduleType,
  toLocalSchedule
} from 'yougile-model'
import {BooleanHookState} from '@containers/hooks'
import {RadioButton} from '../../base/radiobutton'
import {DateInput} from '../../date-picker/date-input'
import {DaysOfMonthPicker} from '../../date-picker/days-of-month-picker'
import {MonthOfYearPicker} from '../../date-picker/month-of-year-picker'
import {Option} from '../../select/select'
import {SingleSelect} from '../../select/single-select'
import {NumberInput} from '../../text-input/text-input'
import {DayOfWeekChooser} from '../day-of-week-chooser'
import {TimeInput} from '../time-input'
import {useScheduleChooser} from './use-schedule-chooser'

interface Props {
  schedule: Schedule
  update: (newSchedule: Schedule) => void
  className?: string
  children?: React.ReactNode
  latestRepeatTime?: number
  valid?: BooleanHookState
  id?: DataObjectId
}

// eslint-disable-next-line max-lines-per-function
const ScheduleChooserBase = (props: Props) => {
  const {
    schedule: scheduleServerTime,
    update: updateServerSchedule,
    className,
    children,
    latestRepeatTime,
    valid
  } = props

  const schedule = toLocalSchedule(scheduleServerTime, true)
  const bem = Util.bemFrom('schedule-chooser', [className])

  const {msg} = Lang
  const typeOpts: Array<Option<ScheduleType>> = [
    {value: 'daily', label: msg.report.daily},
    {value: 'weekly', label: msg.report.weekly},
    {value: 'monthly', label: msg.report.monthly},
    {value: 'yearly', label: msg.report.yearly}
  ]
  const scheduleTypeToEachMsg = {
    ['daily']: [msg.report.eachDay1, msg.report.eachDay2],
    ['weekly']: [msg.report.eachWeek1, msg.report.eachWeek2],
    ['monthly']: [msg.report.eachMonth1, msg.report.eachMonth2],
    ['yearly']: [msg.report.eachYear1, msg.report.eachYear2]
  }
  const eachLabel = scheduleTypeToEachMsg[schedule.type]

  const {
    eachValue,
    timeValid,
    dateValid,
    updateType,
    updateTime,
    updateEach,
    updateMonthsOfYear,
    updateDaysOfMonth,
    updateDaysOfWeek,
    updateStartDate,
    updateSelectedDayNumber,
    updateSelectedDayType,
    onDayOfMonthClick,
    onSelectedDayOfMonthClick,
    selectedDayNumberOptions,
    selectedDayTypeOptions,
    startDate
  } = useScheduleChooser(
    schedule,
    updateServerSchedule,
    latestRepeatTime,
    valid
  )

  return (
    <div className={bem()}>
      <div className={bem('group')}>
        {children}
        <SingleSelect
          className={bem('type-select')}
          options={typeOpts}
          value={schedule.type}
          onChange={updateType}
        />
      </div>

      <div className={bem('group')}>
        <div className={bem('time')} />
        <div className={bem('right_column_margin')} />
        <div>{eachLabel[0]}</div>
        <NumberInput
          className={bem('each-input')}
          onChange={updateEach}
          value={eachValue}
          hasError={!eachValue}
          large
        />
        <div>{eachLabel[1]}</div>
      </div>

      {schedule.type === 'yearly' && (
        <div className={bem('group')}>
          <div className={bem('time')} />
          <div className={bem('right_column_margin')} />
          <MonthOfYearPicker
            value={schedule.monthsOfYear}
            onChange={updateMonthsOfYear}
          />
        </div>
      )}

      {['monthly', 'yearly'].includes(schedule.type) && (
        <>
          <div className={bem('group')}>
            <div className={bem('time')} />
            <div className={bem('right_column_margin')} />
            <RadioButton
              active={!schedule.selectedDayOfMonth}
              onClick={onDayOfMonthClick}
              label={msg.report.onDayOfMonth}
            />
          </div>
          <div className={bem('group mt-0')}>
            <div className={bem('time')} />
            <div className={bem('right_column_margin')} />
            <DaysOfMonthPicker
              value={schedule.daysOfMonth}
              onChange={updateDaysOfMonth}
              disabled={!!schedule.selectedDayOfMonth}
            />
          </div>

          <div className={bem('group')}>
            <div className={bem('time')} />
            <div className={bem('right_column_margin')} />
            <RadioButton
              active={!!schedule.selectedDayOfMonth}
              onClick={onSelectedDayOfMonthClick}
              label={msg.report.onDaySelected}
            />
          </div>
          <div className={bem('group mt-0')}>
            <div className={bem('time')} />
            <div className={bem('right_column_margin')} />
            <SingleSelect
              className={bem('select-day')}
              options={selectedDayNumberOptions}
              value={String(schedule.selectedDayOfMonth?.dayNumber)}
              onChange={updateSelectedDayNumber}
              disabled={!schedule.selectedDayOfMonth}
            />
            <SingleSelect
              className={bem('select-day')}
              options={selectedDayTypeOptions}
              value={String(schedule.selectedDayOfMonth?.dayType)}
              onChange={updateSelectedDayType}
              disabled={!schedule.selectedDayOfMonth}
            />
          </div>
        </>
      )}

      {schedule.type === 'weekly' && (
        <div className={bem('group')}>
          <div className={bem('day')}>{msg.report.day}:</div>
          <div className={bem('week')}>
            <DayOfWeekChooser
              value={schedule.daysOfWeek}
              onChange={updateDaysOfWeek}
            />
          </div>
        </div>
      )}

      <div className={bem('group')}>
        <div className={bem('time')}>{msg.report.time}:</div>
        <TimeInput
          className={bem('time-input')}
          onChange={updateTime}
          hourUTC={schedule.hour}
          minuteUTC={schedule.minute}
          valid={timeValid}
          large
          id={props.id}
        />
      </div>

      <div className={bem('group')}>
        <div className={bem('time')}>{msg.report.startDate}:</div>
        <div className={bem('right_column_margin')} />
        <DateInput
          large
          initialDate={startDate}
          onChoose={updateStartDate}
          valid={dateValid}
        />
      </div>
    </div>
  )
}

export const ScheduleChooser = (props: Props) => {
  if (!isSchedule(props.schedule)) {
    return <div />
  }

  return <ScheduleChooserBase {...props} />
}
